export type PointTypeCategory = 'positive' | 'negative';

export type PointTypeItem = {
  id: string;
  points: number;
  memo: string;
  category: PointTypeCategory;
  /** 세트 아이콘 이모지. 없으면 기본 별(GemStar) */
  emoji?: string;
  createdAt: string;
  updatedAt: string;
};

export type PointTypeSet = {
  positiveTypes: PointTypeItem[];
  negativeTypes: PointTypeItem[];
  levelUpPoints: number;
};

/** 에듀코디 레벨 시스템 설정 */
export type PointLevelSettings = {
  levelUpPoints: number;
  /** 레벨 → 보상 코인 */
  levelCoins: Record<number, number>;
  /** 레벨 → 레벨업 아이템 카드 ID 목록 */
  levelItemCards: Record<number, string[]>;
};

export const emptyPointLevelSettings = (): PointLevelSettings => ({
  levelUpPoints: 50,
  levelCoins: {},
  levelItemCards: {},
});

export type ItemCardType = 'passive' | 'active';
export type ItemCardCategory = 'levelup' | 'assignment' | 'store' | 'classPoint';

export type ItemCard = {
  itemCardId: string;
  cost: number | 'Nocharge';
  type: ItemCardType;
  useCount?: number;
  cardImage?: string;
  name: string;
  memo: string;
  category: ItemCardCategory;
  createdAt: string;
  updatedAt: string;
};

export type ItemCardSet = {
  levelupItem: ItemCard[];
  assignmentItem: ItemCard[];
  storeItem: ItemCard[];
};

/** 아이템 카드 사용자 이미지 라이브러리(노트 payload) */
export type ItemCardUserImage = {
  id: string;
  path: string;
  url: string;
  createdAt: string;
  /** 0..ITEM_CARD_USER_IMAGE_MAX-1 — 40칸 슬롯 위치(빈 칸 포함 순서 유지) */
  slotIndex?: number;
};

/** 학급 포인트 보상 구간: 통합 포인트 ≥ threshold 시 전원에게 cards 발급 */
export type ClassPointRewardTier = {
  id: string;
  thresholdPoints: number;
  cards: ItemCard[];
  createdAt: string;
  updatedAt: string;
};

export type ClassPointSettings = {
  enabled: boolean;
  /**
   * ON: 클래스 모든 그룹 포인트를 합산해 학급 포인트 계산·보상 발급
   * OFF: 그룹 단위로 계산·발급
   */
  integrateAllGroups: boolean;
  rewardTiers: ClassPointRewardTier[];
  /** groupId → 클레임(수령)한 보상 구간 id (통합 모드는 `__all_groups__` 키) */
  grantedTierByGroup: Record<string, string[]>;
};

export const emptyClassPointSettings = (): ClassPointSettings => ({
  enabled: false,
  integrateAllGroups: false,
  rewardTiers: [],
  grantedTierByGroup: {},
});

export type MemberPointRecord = {
  id: string;
  memberId: string;
  pointTypeId: string;
  pointValue: number;
  pointMemo: string;
  pointCategory: PointTypeCategory;
  recordedDate: string; // YYYY-MM-DD
  timeTagLabel?: string;
  createdAt: string;
};

/** 에듀코디: General=G, Levelup=L (Store/Assignment 예약) */
export type CoinRecordType = 'General' | 'Levelup' | 'Store' | 'Assignment';

export type MemberCoinRecord = {
  id: string;
  memberId: string;
  coinValue: number;
  coinType?: CoinRecordType;
  memo?: string;
  recordedDate: string;
  timeTagLabel?: string;
  createdAt: string;
};

/** 아이템 카드 사용 기록 (에듀코디 ItemCardUsageRecord) */
export type ItemUsageRecord = {
  usedAt: string; // ISO
  memo?: string;
};

/** 멤버 보유 아이템 인스턴스 (레벨업 잠금 포함) */
export type MemberItemInstance = {
  id: string;
  memberId: string;
  itemCardId: string;
  category: ItemCardCategory;
  acquiredLevel?: number;
  /** active 아이템 남은 사용 횟수 (없으면 카탈로그 useCount 기준·무제한으로 취급) */
  remainingUseCount?: number;
  /** 액티브 카드 사용 이력 */
  usageHistory?: ItemUsageRecord[];
  isLocked?: boolean;
  acquiredAt: string;
};

export type AssignmentPointKind = 'general' | 'project' | 'routine';
export type AssignmentPointStatus =
  | 'pending'
  | 'confirmed'
  | 'delayed'
  | 'exempted'
  | 'excluded'
  | 'submitted';

/** 과제 보상 이력 (class_member_assignment_rewards 테이블) */
export type MemberAssignmentPoint = {
  id: string;
  memberId: string;
  groupId: string;
  assignmentItemId: string;
  assignmentSubId?: string;
  assignmentKind: AssignmentPointKind;
  assignmentTitle: string;
  status: AssignmentPointStatus;
  earnedPoints: number;
  earnedCoins: number;
  earnedItemIds: string[];
  recordedDate: string;
  memo?: string;
  createdAt: string;
  updatedAt: string;
};

/** 프로필 카드 뽑기로 잠금 해제한 카드 (class_member_profile_cards 테이블) */
export type MemberProfileCardUnlock = {
  id: string;
  memberId: string;
  setId: string;
  slotIndex: number;
  /** admin: 플랫폼 관리자 세트 / workspace: 교사 사용자 세트 */
  sourceKind?: 'admin' | 'workspace';
  /** 보유 수량(중복 뽑기 시 증가). 미설정 시 1로 취급 */
  quantity?: number;
  unlockedAt: string;
};

export type MemberNoteType = '일반' | '학습' | '행동';

export type MemberNoteRecord = {
  id: string;
  memberId: string;
  text: string;
  noteType: MemberNoteType;
  recordedDate: string;
  timeTagLabel?: string;
  createdAt: string;
};

export type GroupMemberPoints = {
  totals: Record<string, number>;
  records: MemberPointRecord[];
  coinTotals: Record<string, number>;
  coinRecords: MemberCoinRecord[];
  notes: MemberNoteRecord[];
  /** 멤버별 이미 보상받은 레벨 (재지급 방지) */
  rewardedLevels?: Record<string, number[]>;
  /** 보유 아이템 인스턴스 */
  itemInstances?: MemberItemInstance[];
  /** 과제 포인트 이력 (행동 포인트와 분리) */
  assignmentPoints?: MemberAssignmentPoint[];
  /** 과제 포인트 합계 (멤버ID → 총점) */
  assignmentTotals?: Record<string, number>;
  /** 프로필 카드 뽑기 보유 이력 */
  profileCardUnlocks?: MemberProfileCardUnlock[];
};

export type PointItemNoteDoc = {
  workspaceId: string;
  gradeId: string;
  pointSet: PointTypeSet;
  itemCards: ItemCardSet;
  /** 아이템 카드 사용자 등록 이미지 라이브러리 */
  itemCardImages?: ItemCardUserImage[];
  levelSettings: PointLevelSettings;
  classPointSettings: ClassPointSettings;
  /** groupId → 멤버 포인트·코인·기록 */
  memberPointsByGroup: Record<string, GroupMemberPoints>;
  updatedAt: string;
};

export const emptyPointTypeSet = (): PointTypeSet => ({
  positiveTypes: [],
  negativeTypes: [],
  levelUpPoints: 50,
});

export const emptyItemCardSet = (): ItemCardSet => ({
  levelupItem: [],
  assignmentItem: [],
  storeItem: [],
});

export const emptyGroupMemberPoints = (): GroupMemberPoints => ({
  totals: {},
  records: [],
  coinTotals: {},
  coinRecords: [],
  notes: [],
  rewardedLevels: {},
  itemInstances: [],
  assignmentPoints: [],
  assignmentTotals: {},
  profileCardUnlocks: [],
});

export function emptyPointItemNoteDoc(
  workspaceId: string,
  gradeId: string
): PointItemNoteDoc {
  return {
    workspaceId,
    gradeId,
    pointSet: emptyPointTypeSet(),
    itemCards: emptyItemCardSet(),
    itemCardImages: [],
    levelSettings: emptyPointLevelSettings(),
    classPointSettings: emptyClassPointSettings(),
    memberPointsByGroup: {},
    updatedAt: new Date().toISOString(),
  };
}

export type ScheduleTimeTag = {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  dayKey: string; // mon|tue|...
};
